import { useMemo, useState, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouter } from '@tanstack/react-router'

import { ErrorState } from '@/components/feedback/error-state'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { Skeleton } from '@/components/feedback/skeleton'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { logoutPortal } from '@/components/layout/app-shell/session-actions'
import { BottomSheet } from '@/components/mobile/bottom-sheet'
import { MobilePageHeader } from '@/components/mobile/page-header'
import { Button } from '@/components/primitives/button'
import { IconButton } from '@/components/primitives/icon-button'
import {
  getDeviceTimezone,
  getSupportedTimezones,
  getTimezoneDisplayOptions,
} from '@/features/shared/timezone/timezone'
import { UserShellPage } from '@/features/user/shell/shell-page'
import { normalizeLanguage, setAppLanguage } from '@/i18n/i18n'

import type { UserProfile } from '../api'
import { TimezoneSelect } from '../timezone-select'
import type { TimezoneSelectOption } from '../timezone-select-options'
import {
  getProfileInitial,
  useAvatarEditor,
  usePasswordEditor,
  useProfileQuery,
  useProfileWorkspace,
} from '../use-profile-page'

const automaticTimezoneValue = '__automatic__'

function CameraIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M3 5.2h2l.8-1.4h4.4l.8 1.4h2A1.2 1.2 0 0 1 14.2 6.4v6A1.2 1.2 0 0 1 13 13.6H3a1.2 1.2 0 0 1-1.2-1.2v-6A1.2 1.2 0 0 1 3 5.2Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.2"
      />
      <path
        d="M8 11a2.1 2.1 0 1 0 0-4.2A2.1 2.1 0 0 0 8 11Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg aria-hidden className="size-3.5" fill="none" viewBox="0 0 16 16">
      <path
        d="M9.7 3.1 12.9 6.3M2.7 10.9 10.9 2.7a1.7 1.7 0 0 1 2.4 2.4l-8.2 8.2-3.1.7.7-3.1Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="m6 4 4 4-4 4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

function ProfileMobileSkeleton() {
  return (
    <div className="grid gap-4" data-testid="profile-mobile-skeleton">
      <Skeleton className="h-[96px] rounded-[14px]" />
      <Skeleton className="h-[260px] rounded-[14px]" />
      <Skeleton className="h-[52px] rounded-[14px]" />
    </div>
  )
}

function BackIcon() {
  return (
    <svg aria-hidden className="size-5" fill="none" viewBox="0 0 24 24">
      <path
        d="m15 18-6-6 6-6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

export function ProfileMobilePage() {
  const { t } = useTranslation('userProfile')
  const navigate = useNavigate()
  const router = useRouter()
  const profileQuery = useProfileQuery()

  // 优先回到进入个人中心前的页面(如某个应用会话),直接打开时回 chat 首页。
  // __TSR_index > 0 表示浏览器 history 里确有站内前项,避免从外部链接进来时返回离开。
  const goBack = () => {
    const tsrIndex = (window.history.state as { __TSR_index?: unknown } | null)?.__TSR_index

    if (typeof tsrIndex === 'number' && tsrIndex > 0) {
      router.history.back()
      return
    }

    void navigate({ to: '/chat' })
  }

  return (
    <UserShellPage activePath="/profile">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <MobilePageHeader
          left={
            <button
              aria-label={t('actions.backToChat')}
              className="flex size-10 items-center justify-center rounded-lg text-[var(--color-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)]"
              onClick={goBack}
              type="button"
            >
              <BackIcon />
            </button>
          }
          title={t('title')}
        />
        <div className="shrink-0 border-b border-[var(--color-divider-subtle)] px-4">
          <span className="inline-flex items-center border-b-2 border-[var(--color-primary)] px-1 pt-3 pb-2.5 text-[15px] leading-5 font-semibold text-[var(--color-primary)]">
            {t('tabs.basic')}
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
          {profileQuery.isError ? (
            <div className="flex min-h-[360px] items-center justify-center">
              <ErrorState
                description={t('loadErrorDescription')}
                onRetry={() => {
                  void profileQuery.refetch()
                }}
                title={t('loadErrorTitle')}
              />
            </div>
          ) : profileQuery.isLoading || !profileQuery.data ? (
            <ProfileMobileSkeleton />
          ) : (
            <ProfileMobileWorkspace
              key={`${profileQuery.data.id}:${profileQuery.data.name}`}
              profile={profileQuery.data}
            />
          )}
        </div>
      </div>
    </UserShellPage>
  )
}

function ProfileMobileWorkspace({ profile }: { profile: UserProfile }) {
  const { i18n } = useTranslation('userProfile')
  const [passwordEditorOpen, setPasswordEditorOpen] = useState(false)
  const language = normalizeLanguage(i18n.language)
  const {
    avatarEditorOpen,
    closeNameEditor,
    deleteAvatarMutation,
    name,
    nameEditorOpen,
    nameError,
    openNameEditor,
    saveProfileMutation,
    saveTimezoneMutation,
    setAvatarEditorOpen,
    setName,
    setNameError,
    submitName,
    t,
    uploadAvatarMutation,
  } = useProfileWorkspace(profile)
  const initial = getProfileInitial(profile)

  return (
    <div className="grid gap-4" data-testid="profile-mobile-page">
      <section className="rounded-[14px] border border-[#ededee] bg-white px-5 py-5">
        <div className="flex min-w-0 items-center gap-4">
          <button
            aria-label={t('avatarEditor.edit')}
            className="group/avatar relative size-[54px] shrink-0 overflow-hidden rounded-full bg-[var(--color-primary)] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
            onClick={() => setAvatarEditorOpen(true)}
            type="button"
          >
            {profile.avatar ? (
              <img
                alt=""
                className="h-full w-full object-cover"
                decoding="async"
                src={profile.avatar}
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-[30px] leading-none">
                {initial}
              </span>
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-[rgba(17,24,39,0.5)] opacity-0 transition-opacity group-active/avatar:opacity-100 group-focus-visible/avatar:opacity-100">
              <CameraIcon />
            </span>
          </button>

          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <h2 className="truncate text-base font-bold text-[var(--color-text)]">
                {profile.name || t('fallbackName')}
              </h2>
              <IconButton
                aria-label={t('nameEditor.edit')}
                className="size-11 shrink-0 text-[#6b7280]"
                onClick={openNameEditor}
                size="md"
              >
                <PencilIcon />
              </IconButton>
            </div>
            <p className="truncate text-xs text-[#6b7280]">{profile.email}</p>
            {profile.email_managed ? (
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t('emailManaged')}</p>
            ) : null}
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[14px] border border-[#ededee] bg-white">
        <h3 className="px-4 py-3 text-[13px] font-semibold text-[var(--color-text-muted)]">
          {t('account.title')}
        </h3>
        <button
          className="flex min-h-[52px] w-full items-center justify-between border-t border-[rgba(26,28,25,0.06)] px-4 text-left text-[15px] text-[var(--color-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--color-focus)]"
          onClick={() => setPasswordEditorOpen(true)}
          type="button"
        >
          <span>{t('password.reset')}</span>
          <span className="text-[#6b7280]">
            <ChevronRightIcon />
          </span>
        </button>
        <MobileTimezoneSetting
          key={profile.timezone ?? automaticTimezoneValue}
          isSaving={saveTimezoneMutation.isPending}
          onSave={(timezone) => saveTimezoneMutation.mutateAsync({ timezone })}
          timezone={profile.timezone ?? null}
        />
        <button
          className="flex min-h-[52px] w-full items-center justify-between border-t border-[rgba(26,28,25,0.06)] px-4 text-left text-[15px] text-[var(--color-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--color-focus)]"
          onClick={() => {
            void setAppLanguage(language === 'zh-Hans' ? 'en-US' : 'zh-Hans')
          }}
          type="button"
        >
          <span>{i18n.t('shell:userMenu.language')}</span>
          <span className="flex items-center gap-1.5 text-[13px] text-[#6b7280]">
            {language === 'zh-Hans'
              ? i18n.t('shell:language.simplifiedChinese')
              : i18n.t('shell:language.english')}
            <ChevronRightIcon />
          </span>
        </button>
      </section>

      <button
        className="flex min-h-[52px] w-full items-center justify-center rounded-[14px] border border-[#ededee] bg-white text-[15px] font-medium text-[#d92d20] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
        onClick={() => logoutPortal('user')}
        type="button"
      >
        {i18n.t('shell:userMenu.logout')}
      </button>

      <AvatarEditorSheet
        avatar={profile.avatar ?? null}
        cropFailedMessage={t('avatarEditor.cropFailed')}
        initial={initial}
        isRemoving={deleteAvatarMutation.isPending}
        isUploading={uploadAvatarMutation.isPending}
        onClose={() => setAvatarEditorOpen(false)}
        onRemove={async () => {
          await deleteAvatarMutation.mutateAsync()
          setAvatarEditorOpen(false)
        }}
        onUpload={async (file) => {
          await uploadAvatarMutation.mutateAsync(file)
          setAvatarEditorOpen(false)
        }}
        open={avatarEditorOpen}
      />
      <NameEditorSheet
        error={nameError}
        isSaving={saveProfileMutation.isPending}
        name={name}
        onChange={(nextName) => {
          setName(nextName)
          if (nameError) {
            setNameError('')
          }
        }}
        onClose={closeNameEditor}
        onSubmit={submitName}
        open={nameEditorOpen}
      />
      <PasswordEditorSheet
        onClose={() => setPasswordEditorOpen(false)}
        open={passwordEditorOpen}
        profileEmail={profile.email}
      />
    </div>
  )
}

function MobileTimezoneSetting({
  isSaving,
  onSave,
  timezone,
}: {
  isSaving: boolean
  onSave: (timezone: string | null) => Promise<UserProfile>
  timezone: string | null
}) {
  const { i18n, t } = useTranslation('userProfile')
  const deviceTimezone = useMemo(() => getDeviceTimezone(), [])
  const locale = i18n.resolvedLanguage ?? i18n.language
  const savedValue = timezone ?? automaticTimezoneValue
  const [selectedValue, setSelectedValue] = useState(savedValue)
  const timezoneOptions = useMemo<TimezoneSelectOption[]>(() => {
    const displayOptions = getTimezoneDisplayOptions(
      getSupportedTimezones([deviceTimezone, timezone]),
      locale,
    )
    const deviceOption = displayOptions.find((option) => option.timezone === deviceTimezone)

    return [
      {
        description: deviceOption
          ? t('timezone.automaticDescription', {
              location: deviceOption.location,
              timezone: deviceTimezone,
            })
          : t('timezone.automaticDescriptionUnavailable'),
        label: deviceOption
          ? t('timezone.automaticWithDevice', { timezone: deviceOption.summaryLabel })
          : t('timezone.automatic'),
        searchText: [t('timezone.automatic'), deviceOption?.searchText, deviceTimezone].join(' '),
        value: automaticTimezoneValue,
      },
      ...displayOptions.map((option) => ({
        description: option.timezone,
        label: option.label,
        searchText: option.searchText,
        value: option.timezone,
      })),
    ]
  }, [deviceTimezone, locale, t, timezone])

  const selectTimezone = async (value: string) => {
    setSelectedValue(value)

    try {
      await onSave(value === automaticTimezoneValue ? null : value)
    } catch {
      setSelectedValue(savedValue)
    }
  }

  return (
    <div className="flex min-h-[52px] w-full items-center justify-between gap-3 border-t border-[rgba(26,28,25,0.06)] px-4 text-[15px] text-[var(--color-text)]">
      <span className="shrink-0">{t('timezone.title')}</span>
      <TimezoneSelect
        ariaLabel={t('timezone.title')}
        className="w-[58vw] max-w-[220px] shrink-0"
        disabled={isSaving}
        emptyText={t('timezone.searchEmpty')}
        onValueChange={(value) => {
          void selectTimezone(value)
        }}
        options={timezoneOptions}
        searchLabel={t('timezone.searchLabel')}
        searchPlaceholder={t('timezone.searchPlaceholder')}
        value={selectedValue}
      />
    </div>
  )
}

function NameEditorSheet({
  error,
  isSaving,
  name,
  onChange,
  onClose,
  onSubmit,
  open,
}: {
  error: string
  isSaving: boolean
  name: string
  onChange: (name: string) => void
  onClose: () => void
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void
  open: boolean
}) {
  const { t } = useTranslation('userProfile')

  return (
    <BottomSheet onClose={onClose} open={open} title={t('nameEditor.title')}>
      <form
        className="flex max-h-[calc(100dvh-108px)] flex-col"
        data-testid="profile-name-sheet"
        onSubmit={onSubmit}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <FormField
            error={error}
            htmlFor="profile-display-name"
            label={t('fields.displayName')}
            required
          >
            <Input
              className="h-11"
              id="profile-display-name"
              invalid={Boolean(error)}
              onChange={(event) => onChange(event.target.value)}
              placeholder={t('nameEditor.placeholder')}
              value={name}
            />
          </FormField>
        </div>
        <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-[var(--color-divider-subtle)] px-4 pt-3 pb-[calc(14px+env(safe-area-inset-bottom))]">
          <Button className="h-11" disabled={isSaving} onClick={onClose} variant="secondary">
            {t('nameEditor.cancel')}
          </Button>
          <Button className="h-11" isLoading={isSaving} type="submit">
            {t('nameEditor.submit')}
          </Button>
        </div>
      </form>
    </BottomSheet>
  )
}

function AvatarEditorSheet({
  avatar,
  cropFailedMessage,
  initial,
  isRemoving,
  isUploading,
  onClose,
  onRemove,
  onUpload,
  open,
}: {
  avatar: string | null
  cropFailedMessage: string
  initial: string
  isRemoving: boolean
  isUploading: boolean
  onClose: () => void
  onRemove: () => Promise<void>
  onUpload: (file: File) => Promise<void>
  open: boolean
}) {
  const {
    closeAndReset,
    cropError,
    draftUrl,
    fileInputRef,
    handleFileChange,
    isBusy,
    previewAvatar,
    removeAvatar,
    setZoom,
    submitAvatar,
    t,
    zoom,
  } = useAvatarEditor({
    avatar,
    cropFailedMessage,
    isRemoving,
    isUploading,
    onClose,
    onRemove,
    onUpload,
  })

  return (
    <BottomSheet onClose={closeAndReset} open={open} title={t('avatarEditor.title')}>
      <div className="flex max-h-[calc(100dvh-108px)] flex-col" data-testid="profile-avatar-sheet">
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-[18px] pb-4">
          <div className="grid justify-items-center gap-4">
            <div className="relative size-[200px] overflow-hidden rounded-full bg-[var(--color-surface-muted)] text-[var(--color-text-subtle)]">
              {previewAvatar ? (
                <img
                  alt={t('avatarEditor.preview')}
                  className="h-full w-full object-cover transition-transform duration-100"
                  decoding="async"
                  src={previewAvatar}
                  style={{ transform: draftUrl ? `scale(${zoom.toFixed(2)})` : undefined }}
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-[64px] leading-none">
                  {initial}
                </span>
              )}
              <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-[rgba(16,24,40,0.1)] ring-inset" />
            </div>

            <label className="grid w-full gap-2 text-[13px] text-[var(--color-text-muted)]">
              <span className="flex items-center justify-between">
                <span>{t('avatarEditor.zoom')}</span>
                <span>{zoom.toFixed(2)}x</span>
              </span>
              <input
                aria-label={t('avatarEditor.zoom')}
                className="h-11 w-full cursor-pointer accent-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!draftUrl || isBusy}
                max="2"
                min="1"
                onChange={(event) => setZoom(Number(event.target.value))}
                step="0.01"
                type="range"
                value={zoom}
              />
            </label>

            {cropError ? (
              <InlineNotice
                className="w-full py-2.5 text-[13px] leading-[19px]"
                role="alert"
                tone="danger"
              >
                {cropError}
              </InlineNotice>
            ) : null}
          </div>
        </div>
        <div className="grid shrink-0 gap-2 border-t border-[var(--color-divider-subtle)] px-4 pt-3 pb-[calc(14px+env(safe-area-inset-bottom))]">
          <label className="relative flex min-h-11 cursor-pointer items-center justify-center rounded-md border border-[var(--color-control-border)] bg-white text-[15px] text-[var(--color-text)]">
            <input
              accept="image/png,image/jpeg,image/gif,image/webp"
              aria-label={t('avatarEditor.choose')}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              disabled={isBusy}
              onChange={handleFileChange}
              ref={fileInputRef}
              type="file"
            />
            {draftUrl ? t('avatarEditor.chooseAnother') : t('avatarEditor.choose')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              className="h-11"
              disabled={!avatar || isBusy}
              isLoading={isRemoving}
              onClick={removeAvatar}
              variant="secondary"
            >
              {t('avatarEditor.remove')}
            </Button>
            <Button
              className="h-11"
              disabled={!draftUrl || isBusy}
              isLoading={isUploading}
              onClick={submitAvatar}
            >
              {t('avatarEditor.save')}
            </Button>
          </div>
        </div>
      </div>
    </BottomSheet>
  )
}

function PasswordEditorSheet({
  onClose,
  open,
  profileEmail,
}: {
  onClose: () => void
  open: boolean
  profileEmail: string
}) {
  const {
    closeAndReset,
    passwordErrors,
    passwordForm,
    passwordMutation,
    submitPassword,
    t,
    updatePasswordField,
  } = usePasswordEditor({ onClose, profileEmail })

  return (
    <BottomSheet onClose={closeAndReset} open={open} title={t('password.modalTitle')}>
      <form
        className="flex max-h-[calc(100dvh-108px)] flex-col"
        data-testid="profile-password-sheet"
        onSubmit={submitPassword}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <div className="grid gap-3.5">
            <InlineNotice
              className="py-2.5 text-[13px] leading-[19px]"
              role="status"
              tone="warning"
            >
              {t('password.reloginNotice')}
            </InlineNotice>
            <FormField
              error={passwordErrors.currentPassword}
              htmlFor="profile-currentPassword"
              label={t('fields.currentPassword')}
              required
            >
              <PasswordInput
                autoComplete="current-password"
                className="h-11"
                hideLabel={t('password.hidePassword')}
                id="profile-currentPassword"
                invalid={Boolean(passwordErrors.currentPassword)}
                onChange={(event) => updatePasswordField('currentPassword', event.target.value)}
                showLabel={t('password.showPassword')}
                value={passwordForm.currentPassword}
              />
            </FormField>
            <FormField
              error={passwordErrors.newPassword}
              htmlFor="profile-newPassword"
              label={t('fields.newPassword')}
              required
            >
              <PasswordInput
                autoComplete="new-password"
                className="h-11"
                hideLabel={t('password.hidePassword')}
                id="profile-newPassword"
                invalid={Boolean(passwordErrors.newPassword)}
                onChange={(event) => updatePasswordField('newPassword', event.target.value)}
                showLabel={t('password.showPassword')}
                value={passwordForm.newPassword}
              />
            </FormField>
            <FormField
              error={passwordErrors.confirmPassword}
              htmlFor="profile-confirmPassword"
              label={t('fields.confirmPassword')}
              required
            >
              <PasswordInput
                autoComplete="new-password"
                className="h-11"
                hideLabel={t('password.hidePassword')}
                id="profile-confirmPassword"
                invalid={Boolean(passwordErrors.confirmPassword)}
                onChange={(event) => updatePasswordField('confirmPassword', event.target.value)}
                showLabel={t('password.showPassword')}
                value={passwordForm.confirmPassword}
              />
            </FormField>
          </div>
        </div>
        <div className="shrink-0 border-t border-[var(--color-divider-subtle)] px-4 pt-3 pb-[calc(14px+env(safe-area-inset-bottom))]">
          <Button className="h-[46px] w-full" isLoading={passwordMutation.isPending} type="submit">
            {t('password.submit')}
          </Button>
        </div>
      </form>
    </BottomSheet>
  )
}
